/* ============================== SHARE LINKS — `/go/{kind}/{id}` ==============================
 *
 * ONE address for a resource that can be pasted anywhere: it names the RESOURCE, not the app the
 * resource lives in. The login origin resolves it once it knows WHO opened it, because the answer
 * depends on the reader — an event is `/events/:id` for a student and a teacher, and
 * `/education/events/:id/edit` for an admin. A link that named an app would be wrong for half the
 * people it is sent to, and a link that named a PATH would be wrong for the other half.
 *
 * ⚠ THIS MODULE IS THE VOCABULARY ONLY — the kinds, the URL shape and the parsing. The table of
 * WHICH app and WHICH path is `linkTargets.ts` beside it (it moved here from apps/login when the
 * notification drawer needed each SPA's own row — NOTIF-DRAWER).
 *
 * ⚠ IT IS SHARED BECAUSE BOTH ENDS OF THE LINK MUST AGREE. Every SPA PUBLISHES these URLs (a Copy
 * link button, `shareUrl` below) and apps/login READS them. Two hand-spelled copies of `/go/event/…`
 * drift the first time either side is edited, and the failure is a 404 on somebody else's machine.
 *
 * ⚠ `go` IS A RESERVED SLUG (siteSlug.ts) AND MUST STAY ONE. The login origin serves the marketing
 * site from `/:slug`, so a page an admin called `go` would collide with this router.
 */

/**
 * The resources a share link may point at.
 *
 * ⚠ ADDING ONE IS TWO EDITS, NOT ONE: the kind here, and its row in `LINK_TARGETS` (linkTargets.ts). A
 * kind with no row parses and then resolves to nothing, which reaches the reader as "this link isn't
 * available for your account" — a truthful message about the wrong thing. `linkTargets.test.ts`
 * pins the two lists against each other for exactly that reason.
 */
// NOTIF-DRAWER phase 2 added the last three: the bell drawer's "Open" for a receipt to review, a role request
// to decide and an appointment reminder. Notification (ZerEduService NotificationShareLinks) writes them.
export const LINK_KINDS = ['event', 'order', 'ticket', 'exam', 'course', 'payment_document', 'role_request', 'appointment'] as const;

export type LinkKind = (typeof LINK_KINDS)[number];

export function isLinkKind(value: string): value is LinkKind {
  return (LINK_KINDS as readonly string[]).includes(value);
}

/** The first path segment the login origin answers share links on. */
export const LINK_ROUTE_PREFIX = 'go';

/** A parsed share link: what is being pointed at, and which one. */
export interface LinkIntent {
  kind: LinkKind;
  id: string;
}

/**
 * ⚠ IDS ARE GUIDS, AND THE CHECK IS NOT DECORATION. Everything after `/go/` is attacker-controlled
 * text that ends up deciding a redirect; refusing anything that is not a GUID means a share link can
 * never carry a path, a scheme or a host. (Every resource in `LINK_KINDS` is keyed by a GUID — the
 * day one is not, this pattern is the thing to revisit, not the redirect guard.)
 */
const ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** A `{kind, id}` pair, or null when either half is not one this build understands. */
export function parseLinkIntent(kind: string | undefined, id: string | undefined): LinkIntent | null {
  if (!kind || !id) return null;
  const value = kind.trim().toLowerCase();
  const target = id.trim();
  if (!isLinkKind(value) || !ID_PATTERN.test(target)) return null;
  return { kind: value, id: target.toLowerCase() };
}

/** The path a share link lives at on the login origin: `/go/event/{id}`. */
export function linkPath(intent: LinkIntent): string {
  return `/${LINK_ROUTE_PREFIX}/${intent.kind}/${intent.id}`;
}

/**
 * The absolute URL to hand somebody — what a Copy link button writes to the clipboard.
 *
 * `loginUrl` is each SPA's `VITE_LOGIN_URL`, which carries a PATH (`https://teeneagle.co.uk/login`),
 * so it is reduced to its origin here rather than at five call sites. A malformed value returns the
 * bare path: a relative link is useless in a paste, but it is visible as wrong, whereas a thrown
 * error would take the page down over a copy button.
 */
export function shareUrl(loginUrl: string, intent: LinkIntent): string {
  const path = linkPath(intent);
  try {
    return new URL(path, new URL(loginUrl).origin).toString();
  } catch {
    return path;
  }
}

/**
 * The same intent as ONE query-string value, for the hop through sign-in: `?link=event:{id}`.
 *
 * ⚠ DELIBERATELY NOT A URL, AND THAT IS THE WHOLE POINT OF THE SEPARATE PARAMETER. `?return=` is an
 * absolute URL checked against the SPA origin allowlist (`isAllowedReturnUrl`), so it cannot carry a
 * path back to the LOGIN origin — and widening that guard to accept one would weaken the open-redirect
 * check for every caller. A kind and a GUID can express nothing but a kind and a GUID.
 */
export function encodeLinkParam(intent: LinkIntent): string {
  return `${intent.kind}:${intent.id}`;
}

export function parseLinkParam(raw: string | null | undefined): LinkIntent | null {
  if (!raw) return null;
  const separator = raw.indexOf(':');
  if (separator < 0) return null;
  return parseLinkIntent(raw.slice(0, separator), raw.slice(separator + 1));
}
